import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import DashboardLayout from "../../components/DashboardLayout";
import { Building2, Search, Save, CheckCircle, AlertCircle, Building, Phone, Mail, MapPin, User, Percent, CreditCard, FileText } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HoldingProfile() {
  const { t } = useTranslation();
  const token = localStorage.getItem("scaffoldx_token");
  
  const [profile, setProfile] = useState(null);
  const [fees, setFees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lookupOrgNumber, setLookupOrgNumber] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  
  // Fetch holding data on mount
  useEffect(() => {
    fetchHolding();
  }, []);
  
  const fetchHolding = async () => {
    try {
      const res = await fetch(`${API_URL}/api/holding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile || {});
        setFees(data.fees || {});
      }
    } catch (error) {
      console.error("Error fetching holding:", error);
      toast.error("Feil ved henting av firmaprofil");
    } finally {
      setLoading(false);
    }
  };
  
  const handleLookup = async () => {
    if (!lookupOrgNumber || lookupOrgNumber.length < 9) {
      toast.error("Vennligst skriv inn et gyldig org.nummer (9 siffer)");
      return;
    }
    
    setLookingUp(true);
    try {
      const res = await fetch(`${API_URL}/api/lookup/company/${lookupOrgNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProfile(prev => ({
          ...prev,
          company_name: data.name,
          org_number: data.org_number,
          address: data.address,
          postal_code: data.postal_code,
          city: data.city,
          country: data.country || "Norway"
        }));
        toast.success("Firmainformasjon hentet fra Brønnøysundregisteret!");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Kunne ikke finne firma");
      }
    } catch (error) {
      console.error("Lookup error:", error);
      toast.error("Feil ved oppslag");
    } finally {
      setLookingUp(false);
    }
  };
  
  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };
  
  const handleFeeChange = (field, value) => {
    setFees(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/holding`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          profile,
          fees
        })
      });
      
      if (res.ok) {
        toast.success("Profil oppdatert!");
        fetchHolding();
      } else {
        toast.error("Kunne ikke oppdatere profil");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Feil ved lagring");
    } finally {
      setSaving(false);
    }
  };
  
  // Check if profile is complete
  const isComplete = profile && 
    profile.company_name && 
    profile.org_number && 
    profile.address && 
    profile.city && 
    profile.email;
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#23F7DD]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#23F7DD]" />
              Holding Firmaprofil
            </h1>
            <p className="text-gray-400 mt-1">
              Administrer holdingselskapets informasjon for kontrakter
            </p>
          </div>
          
          {isComplete ? (
            <div className="flex items-center gap-2 text-green-400 bg-green-400/10 px-4 py-2 rounded-lg">
              <CheckCircle className="w-5 h-5" />
              <span>Komplett profil</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-yellow-400 bg-yellow-400/10 px-4 py-2 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              <span>Ufullstendig profil</span>
            </div>
          )}
        </div>
        
        {/* Lookup Card */}
        <Card className="bg-[#1E293B] border-[#334155]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-[#23F7DD]" />
              Hent fra Brønnøysundregisteret
            </CardTitle>
            <CardDescription>
              Skriv inn organisasjonsnummer for å hente firmainformasjon automatisk
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Org.nummer (9 siffer)"
                value={lookupOrgNumber}
                onChange={(e) => setLookupOrgNumber(e.target.value.replace(/\D/g, "").slice(0, 9))}
                className="bg-[#0F172A] border-[#334155] text-white max-w-xs"
              />
              <Button 
                onClick={handleLookup}
                disabled={lookingUp || lookupOrgNumber.length < 9}
                className="bg-[#23F7DD] text-black hover:bg-[#1ACBB8]"
              >
                {lookingUp ? "Søker..." : "Hent info"}
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Profile Form */}
        <Card className="bg-[#1E293B] border-[#334155]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building className="w-5 h-5 text-[#23F7DD]" />
              Firmainformasjon
            </CardTitle>
            <CardDescription>
              Denne informasjonen brukes i kontrakter med klienter
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Firmanavn *</Label>
                <Input
                  value={profile?.company_name || ""}
                  onChange={(e) => handleProfileChange("company_name", e.target.value)}
                  placeholder="Holding AS"
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Org.nummer *</Label>
                <Input
                  value={profile?.org_number || ""}
                  onChange={(e) => handleProfileChange("org_number", e.target.value.replace(/\D/g, "").slice(0, 9))}
                  placeholder="123456789"
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </div>
            </div>
            
            {/* Address */}
            <div className="space-y-4">
              <h4 className="text-white font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#23F7DD]" />
                Adresse
              </h4>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Gateadresse *</Label>
                <Input
                  value={profile?.address || ""}
                  onChange={(e) => handleProfileChange("address", e.target.value)}
                  placeholder="Gatenavn 123"
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Postnummer</Label>
                  <Input
                    value={profile?.postal_code || ""}
                    onChange={(e) => handleProfileChange("postal_code", e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="0001"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">By *</Label>
                  <Input
                    value={profile?.city || ""}
                    onChange={(e) => handleProfileChange("city", e.target.value)}
                    placeholder="Oslo"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Land</Label>
                  <Input
                    value={profile?.country || "Norway"}
                    onChange={(e) => handleProfileChange("country", e.target.value)}
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
              </div>
            </div>
            
            {/* Contact */}
            <div className="space-y-4">
              <h4 className="text-white font-medium flex items-center gap-2">
                <User className="w-4 h-4 text-[#23F7DD]" />
                Kontaktinformasjon
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Daglig leder *</Label>
                  <Input
                    value={profile?.legal_representative || ""}
                    onChange={(e) => handleProfileChange("legal_representative", e.target.value)}
                    placeholder="Navn Navnesen"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Stilling</Label>
                  <Input
                    value={profile?.legal_rep_title || "Daglig leder"}
                    onChange={(e) => handleProfileChange("legal_rep_title", e.target.value)}
                    placeholder="Daglig leder"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> E-post *
                  </Label>
                  <Input
                    type="email"
                    value={profile?.email || ""}
                    onChange={(e) => handleProfileChange("email", e.target.value)}
                    placeholder="post@holding.no"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Telefon
                  </Label>
                  <Input
                    value={profile?.phone || ""}
                    onChange={(e) => handleProfileChange("phone", e.target.value)}
                    placeholder="+47 123 45 678"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
              </div>
            </div>
            
            {/* Bank Details */}
            <div className="space-y-4">
              <h4 className="text-white font-medium flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-[#23F7DD]" />
                Bankdetaljer
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Banknavn</Label>
                  <Input
                    value={profile?.bank_name || ""}
                    onChange={(e) => handleProfileChange("bank_name", e.target.value)}
                    placeholder="DNB"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Kontonummer</Label>
                  <Input
                    value={profile?.bank_account || ""}
                    onChange={(e) => handleProfileChange("bank_account", e.target.value)}
                    placeholder="1234.56.78901"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Fee Configuration */}
        <Card className="bg-[#1E293B] border-[#334155]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Percent className="w-5 h-5 text-[#23F7DD]" />
              Gebyrinnstillinger
            </CardTitle>
            <CardDescription>
              Konfigurer standardgebyrer for prosjekter
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Platform fee (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={fees?.platform_fee || 2}
                  onChange={(e) => handleFeeChange("platform_fee", e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
                <p className="text-xs text-gray-500">Går til AllStillas Platform</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Holding margin (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="20"
                  value={fees?.holding_margin || 5}
                  onChange={(e) => handleFeeChange("holding_margin", e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
                <p className="text-xs text-gray-500">Standard margin for drift</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Andre kostnader (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={fees?.andre_kostnader || 3}
                  onChange={(e) => handleFeeChange("andre_kostnader", e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
                <p className="text-xs text-gray-500">Pensjon, forsikring etc.</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-[#334155]">
              <div className="space-y-2">
                <Label className="text-gray-300">AGA - Arbeidsgiveravgift (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="20"
                  value={fees?.aga || 14.5}
                  onChange={(e) => handleFeeChange("aga", e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Feriepenger (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="15"
                  value={fees?.feriepenger || 10.8}
                  onChange={(e) => handleFeeChange("feriepenger", e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#23F7DD] text-black hover:bg-[#1ACBB8] gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? "Lagrer..." : "Lagre endringer"}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
